import React, { useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import {
  HubBackButton,
  HubButton,
  HubHookFormDatePicker,
} from "../../components";
import { useScheduleContext } from "../../hooks";

import {
  StyledHubButtonGroup,
  StyledTitledSubStack,
  StyledTitleStack,
} from "./styles";
import { GRAPH_TAB } from "./constants";
import { tabs } from "./helper";
import {
  MODE_SCHEDULER_GRAPH,
  MODE_SCHEDULER_TIMELINE,
  endDateDisabler,
  getDefaultRange,
  onEndChange,
  onStartChange,
  resolveRange,
  startDateDisabler,
  toPickerValue,
} from "./dateHelper";

const resolveMode = (mode, tab) => {
  if (mode !== "scheduler") return mode;
  return tab === GRAPH_TAB ? MODE_SCHEDULER_GRAPH : MODE_SCHEDULER_TIMELINE;
};

const ScheduleBar = ({
  mode = MODE_SCHEDULER_TIMELINE,
  hideTabs = false,
  useExport,
  controls,
  children,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab, setTab = () => {} } = useScheduleContext();

  const effectiveMode = useMemo(() => resolveMode(mode, tab), [mode, tab]);

  const { startDate, endDate } = resolveRange(searchParams, effectiveMode);

  const writeRange = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => next.set(k, v));
      return next;
    });
  };

  const onDateChange = (val, field) => {
    if (!val) return;
    const updates =
      field === "startDate"
        ? onStartChange(val, effectiveMode)
        : onEndChange(val, effectiveMode);
    writeRange(updates);
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    if (newTab === GRAPH_TAB && mode === "scheduler") {
      const locked = onStartChange(startDate, MODE_SCHEDULER_GRAPH);
      writeRange(locked);
    }
  };

  const handleReset = () => writeRange(getDefaultRange(effectiveMode));

  return (
    <StyledTitleStack>
      <StyledTitledSubStack disableYPadding>
        <HubBackButton />
        {!hideTabs && (
          <StyledHubButtonGroup
            buttons={tabs}
            selected={tab}
            setSelected={handleTabChange}
            buttonProps={{ variant: "outlined" }}
            allowNeutral
          />
        )}
        {children}
      </StyledTitledSubStack>
      <StyledTitledSubStack>
        <Stack direction="row" gap="25px" alignItems="center">
          <Stack direction="row" gap="10px" alignItems="center">
            <HubButton
              variant="text"
              size="xsmall"
              icon="RefreshCw"
              onClick={handleReset}
              iconProps={{ size: "12px" }}
            />
            {controls ? (
              controls
            ) : (
              <>
                <Typography variant="caption11">From</Typography>
                <HubHookFormDatePicker
                  name="startDate"
                  defaultValue={toPickerValue(startDate)}
                  onChange={(val) => onDateChange(val, "startDate")}
                  required
                  shouldDisableDate={startDateDisabler(effectiveMode)}
                />
                <Typography variant="caption11">To</Typography>
                <HubHookFormDatePicker
                  name="endDate"
                  defaultValue={toPickerValue(endDate)}
                  onChange={(val) => onDateChange(val, "endDate")}
                  required
                  shouldDisableDate={endDateDisabler(effectiveMode)}
                />
              </>
            )}
          </Stack>
        </Stack>
        {useExport && (
          <HubButton
            variant="contained"
            color="neutral"
            size="xsmall"
            icon="Download"
            label="Download"
            onClick={() => window.print()}
            iconProps={{ size: "10px", sx: { marginRight: "5px" } }}
          />
        )}
      </StyledTitledSubStack>
    </StyledTitleStack>
  );
};

export default ScheduleBar;
